import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updatePDPTab from "@NA/Handlers/ADIUpdatePDPtabs";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import * as React from "react";
import ADIColors from "@NA/Utils/ADIColors";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    currentId?: string;
    tabsVisibility?: any;
    currentActiveTab?: string;
}

const mapDispatchToProps = {
    updatePDPTab,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    activeTab: state.ADIGlobalData.updatePDPTab.currentActiveTab,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ADIPdpTabsStyling {
    mainContainer?: InjectableCss;
    container?: InjectableCss;
    tab?: InjectableCss;
    activeTab?: InjectableCss;
    inactiveTab?: InjectableCss;
    tabText?: TypographyProps;
    inactiveTabText?: TypographyProps;
    activeTabText?: TypographyProps;
    divider?: TypographyProps;
}

export const adiPdpTabsStyles: ADIPdpTabsStyling = {
    container: {
        css: css`
            width: 654px !important;
            display: flex;
            padding: 1px 1px !important;
            border-radius: 6px;
            border: solid 1px ${getColor("text.disabled")}!important;
            background: ${getColor("background.contrast")}!important;
            float: left;
            justify-content: center;
            margin: 8px 0px 0px 0px;
            height: 45px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: flex;
                    `,
                ])}
        `,
    },
    tab: {
        css: css`
            margin-bottom: 0 !important;
            list-style: none;
            float: left;
            display: flex;
            width: 33%;
            height: 43px;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            cursor: pointer;
        `,
    },
    activeTab: {
        css: css`
            align-self: stretch;
            flex-grow: 1;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            border-radius: 4px;
            background-color: ${ADIColors.darkBlue1} !important;
            cursor: pointer;
        `,
    },
    inactiveTab: {
        css: css`
            align-self: stretch;
            flex-grow: 1;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            border-radius: 4px;
            cursor: default;
        `,
    },
    tabText: {
        css: css`
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            color: ${ADIColors.darkBlue1};
            width: 100% !important;
        `,
    },
    activeTabText: {
        css: css`
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            color: ${ADIColors.white} !important;
            cursor: default;
        `,
    },
    inactiveTabText: {
        css: css`
            color: ${ADIColors.gray3};
            cursor: default;
            font-size: 18px;
            font-weight: bold;
        `,
    },
    divider: {
        css: css`
            width: 1px;
            height: 26px;
            margin: 6px 0 6px 1px;
            border-radius: 1px;
            background-color: ${ADIColors.gray7};
        `,
    },
};

const styles = adiPdpTabsStyles;

const ADIPdpTabs = (props: Props) => {
    const { currentId, tabsVisibility, currentActiveTab, updatePDPTab } = props;
    const { overViewTab, specificationTab, documentsTab } = tabsVisibility;

    const isProgrammaticScroll = React.useRef(false);

    const checkInActiveTabs = (elem: string) => {
        if (elem === "OverviewTitle" && overViewTab) return true;
        if (elem === "SpecificationsTitle" && specificationTab) return true;
        if (elem === "DocumentsTitle" && documentsTab) return true;
        return false;
    };

    const handleScrollToTheElement = (elem: string) => {
        const targetElement = document.querySelector(`[data-test-selector="${elem}"]`);
        if (checkInActiveTabs(elem)) return;

        if (targetElement) {
            const start = window.scrollY;
            const target = targetElement.getBoundingClientRect().top + window.scrollY - 230;
            const duration = 800;
            let startTime: number = 0;

            isProgrammaticScroll.current = true;

            function animateScroll(currentTime: number) {
                if (!startTime) startTime = currentTime;
                const progress = currentTime - startTime;
                const easeInOutQuad = (progress: number) =>
                    progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;

                window.scrollTo(0, start + (target - start) * easeInOutQuad(progress / duration));

                if (progress < duration) {
                    requestAnimationFrame(animateScroll);
                } else {
                    isProgrammaticScroll.current = false;
                    updatePDPTab({ updateTab: elem });
                }
            }

            requestAnimationFrame(animateScroll);
        }
    };

    React.useEffect(() => {
        const handleScroll = () => {
            if (isProgrammaticScroll.current) return;

            const start = window.scrollY;
            const targetOverviewElement = document.querySelector('[data-test-selector="OverviewTitle"]');
            const targetSpecificElement = document.querySelector('[data-test-selector="SpecificationsTitle"]');
            const targetDocElement = document.querySelector('[data-test-selector="DocumentsTitle"]');

            if (targetOverviewElement) {
                const target = targetOverviewElement.getBoundingClientRect().top + window.scrollY - 265;
                if (target <= start && overViewTab) {
                    updatePDPTab({ updateTab: `OverviewTitle` });
                }
            }
            if (targetSpecificElement) {
                const target = targetSpecificElement.getBoundingClientRect().top + window.scrollY - 265;
                if (target <= start && specificationTab) {
                    updatePDPTab({ updateTab: `SpecificationsTitle` });
                }
            }
            if (targetDocElement) {
                const target = targetDocElement.getBoundingClientRect().top + window.scrollY - 265;
                if (target <= start && documentsTab) {
                    updatePDPTab({ updateTab: `DocumentsTitle` });
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [overViewTab, specificationTab, documentsTab, updatePDPTab]);

    return (
        <StyledWrapper {...styles.container} id={currentId} className="pdptabs">
            <StyledWrapper
                {...(overViewTab
                    ? styles.inactiveTab
                    : currentActiveTab === "OverviewTitle"
                      ? styles.activeTab
                      : styles.tab)}
                onClick={() => handleScrollToTheElement("OverviewTitle")}
            >
                <Typography
                    {...(overViewTab
                        ? styles.inactiveTabText
                        : currentActiveTab === "OverviewTitle"
                          ? styles.activeTabText
                          : styles.tabText)}
                >
                    {translate("Overview")}
                </Typography>
            </StyledWrapper>
            <Typography {...styles.divider}></Typography>
            <StyledWrapper
                {...(specificationTab
                    ? styles.inactiveTab
                    : currentActiveTab === "SpecificationsTitle"
                      ? styles.activeTab
                      : styles.tab)}
                onClick={() => handleScrollToTheElement("SpecificationsTitle")}
            >
                <Typography
                    {...(specificationTab
                        ? styles.inactiveTabText
                        : currentActiveTab === "SpecificationsTitle"
                          ? styles.activeTabText
                          : styles.tabText)}
                >
                    {translate("Specifications")}
                </Typography>
            </StyledWrapper>
            <Typography {...styles.divider} />
            <StyledWrapper
                {...(documentsTab
                    ? styles.inactiveTab
                    : currentActiveTab === "DocumentsTitle"
                      ? styles.activeTab
                      : styles.tab)}
                onClick={() => handleScrollToTheElement("DocumentsTitle")}
            >
                <Typography
                    {...(documentsTab
                        ? styles.inactiveTabText
                        : currentActiveTab === "DocumentsTitle"
                          ? styles.activeTabText
                          : styles.tabText)}
                >
                    {translate("Documents")}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ADIPdpTabs);
